import React, { useState } from 'react';
import { Select, message } from 'antd';
import { BankOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';

/**
 * Header company switcher for multi-membership users.
 * Re-issues JWT and clears React Query cache to avoid cross-company stale data.
 */
export const CompanySwitcher: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const switchCompany = useAuthStore((s) => s.switchCompany);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [switching, setSwitching] = useState(false);

  const memberships = user?.memberships || [];
  if (memberships.length < 2) {
    return null;
  }

  const onChange = async (companyId: string) => {
    if (!companyId || companyId === user?.company_id) return;
    setSwitching(true);
    try {
      await switchCompany(companyId);
      await queryClient.clear();
      message.success('Switched company');
      navigate('/dashboard', { replace: true });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not switch company');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <Select
      aria-label="Active company"
      value={user?.company_id}
      onChange={onChange}
      loading={switching}
      disabled={switching}
      style={{ minWidth: 180, maxWidth: 280 }}
      suffixIcon={<BankOutlined />}
      options={memberships.map((m) => ({
        value: m.company_id,
        label: `${m.name}${m.is_parent ? ' (home)' : ''}`,
      }))}
    />
  );
};

export default CompanySwitcher;
